import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Clock } from "lucide-react";
import { FINAL_CTA, INFO_EMAIL } from "../mock";
import { Reveal, DarkCTA } from "../components/Shared";
import { api, mediaUrl } from "../api";
import { Seo } from "../components/Seo";

let cache = null;
let cachedAt = 0;
export const fetchPosts = async () => {
  if (cache && Date.now() - cachedAt < 60000) return cache;
  const { data } = await api.get("/blog");
  cache = data;
  cachedAt = Date.now();
  return data;
};

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export const PostCard = ({ post, delay = 0, large = false }) => (
  <Reveal delay={delay} className="h-full">
    <Link
      to={`/blog/${post.slug}`}
      className={`card card-hover p-3 flex h-full group ${large ? "flex-col lg:flex-row" : "flex-col"}`}
      data-testid="post-card"
    >
      <div className={`img-tile img-tile-contain aspect-[16/9] ${large ? "lg:w-[55%] lg:self-center" : ""}`}>
        <img src={mediaUrl(post.cover)} alt={post.title} loading="lazy" />
      </div>
      <div className={`p-4 flex-1 flex flex-col ${large ? "lg:p-8 lg:justify-center" : ""}`}>
        <div className="flex items-center gap-3 text-xs text-[var(--fr-muted)] flex-wrap">
          <span className="chip">{post.category}</span>
          <span>{formatDate(post.date)}</span>
          <span className="inline-flex items-center gap-1">
            <Clock size={12} /> {post.readTime}
          </span>
        </div>
        <h3 className={`font-display font-bold tracking-tight leading-snug mt-3 ${large ? "text-2xl md:text-3xl" : "text-[17px]"}`}>
          {post.title}
        </h3>
        <p className={`mt-3 text-[var(--fr-ink-2)] leading-relaxed ${large ? "text-base" : "text-sm line-clamp-3"}`}>{post.excerpt}</p>
        <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fr-primary-dark)] group-hover:gap-2 transition-[gap]">
          Read article <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  </Reveal>
);

const Blog = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [all, setAll] = useState(null);
  const [limit, setLimit] = useState(24);

  useEffect(() => {
    fetchPosts().then(setAll).catch(() => setAll([]));
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set((all || []).map((p) => p.category)))], [all]);

  const posts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...(all || [])]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .filter((p) => category === "All" || p.category === category)
      .filter((p) => !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q));
  }, [all, query, category]);

  useEffect(() => setLimit(24), [query, category]);

  const [featured, ...rest] = posts;
  const filtering = query.trim() || category !== "All";

  return (
    <>
      <Seo title="Blog — Ideas for people who move things" description="Practical thinking on last-mile delivery, fleet operations and the technology reshaping logistics in the GCC and beyond." />
      <section className="pt-16 pb-10 md:pt-24 md:pb-12">
        <div className="container-x">
          <span className="eyebrow-pill fade-up">Blog</span>
          <h1 className="h-display mt-6 max-w-4xl fade-up delay-1">Ideas for people who move things.</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">
            Practical thinking on last-mile delivery, fleet operations and the technology reshaping logistics in the GCC and beyond.
          </p>

          <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 fade-up delay-3">
            <div className="flex flex-wrap gap-2" data-testid="blog-categories">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
                    category === c
                      ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]"
                      : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
                  }`}
                  data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <label className="relative md:ml-auto md:w-72">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fr-muted)]" />
              <input
                className="fr-input !rounded-full !pl-10 !h-10"
                placeholder="Search articles"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="blog-search"
              />
            </label>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          {all === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="card p-3 animate-pulse">
                  <div className="aspect-[16/10] rounded-xl bg-[var(--fr-bg-alt)]" />
                  <div className="h-4 mt-5 w-2/3 rounded bg-[var(--fr-bg-alt)]" />
                  <div className="h-3 mt-3 w-full rounded bg-[var(--fr-bg-alt)]" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="card p-12 text-center" data-testid="blog-empty">
              <h3 className="font-display font-bold text-xl">No articles match “{query}”</h3>
              <p className="mt-2 text-[var(--fr-ink-2)]">Try a different keyword or category.</p>
              <button
                type="button"
                className="btn btn-outline mt-6"
                onClick={() => {
                  setQuery("");
                  setCategory("All");
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {!filtering && featured && (
                <div className="mb-6" data-testid="blog-featured">
                  <PostCard post={featured} large />
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
                {(filtering ? posts : rest).slice(0, limit).map((p, i) => (
                  <PostCard key={p.slug} post={p} delay={(i % 3) * 70} />
                ))}
              </div>
              {(filtering ? posts : rest).length > limit && (
                <div className="mt-10 text-center">
                  <button type="button" className="btn btn-outline btn-lg" onClick={() => setLimit((l) => l + 24)} data-testid="blog-load-more">
                    Load more articles ({(filtering ? posts : rest).length - limit} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <DarkCTA
        title="Get the next article in your inbox."
        text="One email a month with what's changing in last-mile delivery — no fluff, no spam."
        cta={{ label: "Talk to us", to: "/contact" }}
        secondary={FINAL_CTA.cta}
        email={INFO_EMAIL}
      />
    </>
  );
};

export default Blog;
